/**
 * Semprion HQ — Support Command Center
 *
 * 6 tabs: Conversations · Tickets · Knowledge Base · Library · Macros · Config.
 * Top metric strip: Active / Escalated / Avg msgs / CSAT / AI Deflection /
 * Macros count / Open Tickets.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  MessageCircle, AlertTriangle, Star, Zap, BookOpen, Ticket as TicketIcon,
  Settings as SettingsIcon, Loader2, Send, Tag as TagIcon, Trash2, Plus,
  UserCheck, Library, X, StickyNote, Clock, CheckCircle2, RefreshCw, PenLine,} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Inline-edit text input used by table cells in this page (ticket
// subject, KB titles, etc.). Click → input. Save on blur (Enter).
// Esc reverts. Mirrors the contract of the shared
// components/inline/InlineCells.js primitives but we keep a local
// copy here to avoid a cross-package import.
function InlineSubjectInput({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (!editing) setDraft(value || ''); }, [value, editing]);
  useEffect(() => { if (editing && ref.current) { ref.current.focus(); try { ref.current.select(); } catch {} } }, [editing]);
  const commit = async () => {
    if (draft === (value || '')) { setEditing(false); return; }
    setBusy(true);
    try { await onSave(draft); setEditing(false); }
    catch { /* leave editing for retry */ }
    finally { setBusy(false); }
  };
  if (!editing) return <span onClick={() => setEditing(true)} className="cursor-text">{value || <span className="text-muted-foreground">—</span>}</span>;
  return (
    <Input ref={ref} value={draft} onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
        if (e.key === 'Escape') { e.preventDefault(); setDraft(value || ''); setEditing(false); }
      }}
      onClick={e => e.stopPropagation()}
      disabled={busy}
      className="h-7 text-xs bg-card border-amber-500/60 text-foreground"
    />
  );
}

const TABS = [
  { id: 'conversations', label: 'Conversations', Icon: MessageCircle },
  { id: 'tickets',       label: 'Tickets',       Icon: TicketIcon },
  { id: 'kb',            label: 'Knowledge Base',Icon: BookOpen },
  { id: 'library',       label: 'Library',       Icon: Library },
  { id: 'macros',        label: 'Macros',        Icon: Zap },
  { id: 'config',        label: 'Config',        Icon: SettingsIcon },
];

export default function SupportCommandCenter() {
  const [tab, setTab] = useState('conversations');
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/helpdesk/admin/enhanced-stats`);
      setStats(data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  // The header says "Live", so it has to actually be live: the strip refreshes
  // itself on a slow timer, and Refresh is there for when an agent wants it now.
  const [refreshing, setRefreshing] = useState(false);
  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    try { await loadStats(); } finally { setTimeout(() => setRefreshing(false), 400); }
  }, [loadStats]);
  useEffect(() => {
    const id = setInterval(() => { loadStats(); }, 30000);
    return () => clearInterval(id);
  }, [loadStats]);

  return (
    <div className="space-y-4" data-testid="support-command-center">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white flex items-center justify-center shrink-0">
            <MessageCircle className="h-6 w-6" />
          </div>
          <div>
            <div className="text-3xl font-bold tracking-tight">Support Command Center</div>
            <div className="text-sm text-muted-foreground">
              Monitor conversations, manage tickets, train the AI assistant
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* "Live" states that the page refreshes itself; without it an
              operator cannot tell a quiet queue from a stale one. */}
          <span className="inline-flex items-center gap-1.5 text-xs rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-2.5 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Live
          </span>
          <Button variant="outline" size="sm" onClick={refreshAll} data-testid="scc-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Metric strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <MetricCard label="Conversations" value={stats?.active ?? '—'} Icon={MessageCircle}
          color="text-indigo-600" tint="bg-indigo-50"
          sub={`${stats?.active ?? 0} active · ${stats?.escalated ?? 0} escalated`} />
        <MetricCard label="Tickets" value={stats?.open_tickets ?? '—'} Icon={TicketIcon}
          color="text-amber-600" tint="bg-amber-50" badge={stats?.open_tickets || null}
          sub={`${stats?.open_tickets ?? 0} open`} />
        <MetricCard label="Avg Msgs / Chat" value={stats?.avg_msgs_per_chat ?? '—'} Icon={Send}
          color="text-emerald-600" tint="bg-emerald-50"
          sub={`${stats?.resolved ?? 0} resolved`} />
        <MetricCard label="CSAT Score" value={stats?.csat_score ?? '—'} Icon={Star}
          color="text-amber-600" tint="bg-amber-50"
          sub={`${stats?.csat_happy ?? 0} happy · ${stats?.csat_unhappy ?? 0} unhappy`} />
        <MetricCard label="Escalated" value={stats?.escalated ?? '—'} Icon={AlertTriangle}
          color="text-rose-600" tint="bg-rose-50" sub="waiting for a person" />
        <MetricCard label="AI Deflection" value={stats ? `${stats.ai_deflection_rate}%` : '—'} Icon={Zap}
          color="text-teal-600" tint="bg-teal-50" sub="handled without a person" />
        <MetricCard label="Macros" value={stats?.macros_count ?? '—'} Icon={BookOpen}
          color="text-violet-600" tint="bg-violet-50" sub="Quick reply templates" />
      </div>

      {/* Tabs */}
      {/* Tabs — BOP's white pill for the active one rather than an underline,
          with a count badge where there is something waiting. */}
      <div className="flex gap-1 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.Icon;
          const isActive = tab === t.id;
          const count = TAB_COUNTS[t.id]?.(stats) || 0;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition ${
                isActive
                  ? 'bg-card shadow-sm border border-border font-medium text-foreground'
                  : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`support-tab-${t.id}`}>
              <Icon className="h-4 w-4" /> {t.label}
              {count ? (
                <span className={`h-5 min-w-5 px-1.5 rounded-full text-[10px] font-semibold flex items-center justify-center ${
                  t.id === 'tickets' ? 'bg-amber-500 text-white' : 'bg-indigo-600 text-white'}`}>
                  {count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div>
        {tab === 'conversations' && <ConversationsPane onChange={loadStats} />}
        {tab === 'tickets'       && <TicketsPane />}
        {tab === 'kb'            && <KBPane />}
        {tab === 'library'       && <LibraryPane />}
        {tab === 'macros'        && <MacrosPane onChange={loadStats} />}
        {tab === 'config'        && <ConfigPane onChange={loadStats} />}
      </div>
    </div>
  );
}

/** BOP's metric card: a pastel icon tile, the figure, a coloured label, then a
 *  sub-line of the detail behind the figure. The sub-line is the part that
 *  makes the strip readable -- "4" means little, "4 open  0 in prog" means
 *  something. `badge` shows a count chip top-right, as BOP does on Tickets. */
function MetricCard({ label, value, Icon, color = 'text-indigo-600',
                      tint = 'bg-indigo-50', sub, badge }) {
  return (
    <Card className="bg-card border-border relative">
      <CardContent className="p-3">
        <div className="flex items-start justify-between">
          <div className={`h-8 w-8 rounded-lg ${tint} flex items-center justify-center`}>
            <Icon className={`h-4 w-4 ${color}`} />
          </div>
          {badge ? (
            <span className="h-5 min-w-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-semibold flex items-center justify-center">
              {badge}
            </span>
          ) : null}
        </div>
        <div className="text-2xl font-bold text-foreground mt-2 leading-none">{value}</div>
        <div className={`text-xs mt-1 ${color}`}>{label}</div>
        {sub ? <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div> : null}
      </CardContent>
    </Card>
  );
}

/** Which tabs carry a waiting-count badge, and where the number comes from.
 *  Only counts that mean "something needs you" get a badge: a macro count is
 *  information, not a queue. */
const TAB_COUNTS = {
  conversations: (st) => st?.active || 0,
  tickets: (st) => st?.open_tickets || 0,
};

/** One conversation in the list, built like BOP's: an avatar initial, the
 *  person with their organization, the subject, a greyed preview of the last
 *  message, then status, ticket number and date. The preview is what lets an
 *  agent triage a queue without opening every thread. */
function ConversationRow({ c, selected, onOpen }) {
  const who = c.user_name || c.user_email || 'Unknown';
  const initial = (who.trim()[0] || '?').toUpperCase();
  const org = c.organization_name || c.org_name || null;
  const last = (c.messages && c.messages.length)
    ? c.messages[c.messages.length - 1]
    : null;
  const preview = last
    ? `${last.role === 'user' ? '' : 'Bot: '}${(last.content || '').replace(/\s+/g, ' ').slice(0, 70)}`
    : null;
  let when = '';
  try {
    when = c.updated_at ? new Date(c.updated_at).toLocaleDateString() : '';
  } catch { when = ''; }

  return (
    <button onClick={onOpen}
      className={`w-full text-left px-3 py-3 border-b border-border/60 hover:bg-muted/40 ${selected ? 'bg-muted/70' : ''}`}
      data-testid={`convo-${c.id}`}>
      <div className="flex items-start gap-2.5">
        <div className="h-8 w-8 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold flex items-center justify-center shrink-0">
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="text-sm font-medium truncate text-foreground">
              {who}{org ? <span className="text-muted-foreground font-normal"> ({org})</span> : null}
            </div>
            {when && <span className="text-[10px] text-muted-foreground shrink-0">{when}</span>}
          </div>
          <div className="text-xs text-foreground/80 truncate mt-0.5">{c.subject || '—'}</div>
          {preview && (
            <div className="text-[11px] text-muted-foreground truncate mt-0.5">{preview}</div>
          )}
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <StatusPill status={c.status} />
            {c.ticket_number ? (
              <span className="text-[10px] text-muted-foreground"># {c.ticket_number}</span>
            ) : null}
            {(c.tags || []).slice(0, 2).map(t => (
              <span key={t} className="text-[9px] bg-muted text-muted-foreground px-1 rounded">#{t}</span>
            ))}
            {c.user_language && c.user_language !== 'en' && (
              <span className="text-[9px] text-violet-600 uppercase">{c.user_language}</span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

// ---- Conversations ---------------------------------------------------------

function ConversationsPane({ onChange }) {
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [macros, setMacros] = useState([]);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ status, q });
    const { data } = await axios.get(`${API_URL}/api/helpdesk/conversations?${params}`);
    setConvos(data.conversations || []);
  }, [status, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API_URL}/api/helpdesk/canned-responses`).then(({ data }) => setMacros(data.macros || [])).catch(() => {});
  }, []);

  const openConvo = async (id) => {
    const { data } = await axios.get(`${API_URL}/api/helpdesk/conversations/${id}`);
    setActive(data);
  };

  return (
    <div className="grid grid-cols-[340px_1fr] gap-3 h-[calc(100vh-340px)] min-h-[420px]">
      {/* List */}
      <Card className="bg-card border-border flex flex-col">
        <div className="p-2 border-b border-border space-y-1.5">
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search conversations…" className="bg-background border-border h-8 text-xs" data-testid="convo-search" />
          <div className="flex gap-1">
            {['all', 'active', 'escalated', 'resolved'].map(s => (
              <button key={s} onClick={() => setStatus(s)}
                className={`flex-1 text-[10px] py-1 rounded ${status === s ? 'bg-blue-600 text-white' : 'bg-muted text-muted-foreground'}`}
                data-testid={`convo-filter-${s}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {convos.length === 0 && <div className="p-4 text-center text-xs text-muted-foreground">No conversations.</div>}
          {convos.map(c => (
            <ConversationRow key={c.id} c={c} selected={active?.id === c.id}
              onOpen={() => openConvo(c.id)} />
          ))}
        </div>
      </Card>

      {/* Detail */}
      {active
        ? <ConversationDetail convo={active} setConvo={setActive} macros={macros} onChange={() => { load(); onChange?.(); }} />
        : (
          <Card className="bg-card border-border flex items-center justify-center">
            <div className="text-center py-12">
              <div className="h-14 w-14 rounded-xl bg-muted flex items-center justify-center mx-auto mb-3">
                <MessageCircle className="h-7 w-7 text-muted-foreground/60" />
              </div>
              <div className="text-sm font-medium text-foreground">Select a conversation</div>
              <div className="text-xs text-muted-foreground mt-1">
                Click on a conversation to view the full thread
              </div>
            </div>
          </Card>
        )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active:    'bg-blue-500/15 text-blue-400',
    escalated: 'bg-amber-500/15 text-amber-400',
    resolved:  'bg-emerald-500/15 text-emerald-400',
  };
  return <Badge className={`${map[status] || 'bg-zinc-400'} text-[9px] uppercase`}>{status}</Badge>;
}

function ConversationDetail({ convo, setConvo, macros, onChange }) {
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [showMacros, setShowMacros] = useState(false);

  const refresh = async () => {
    const { data } = await axios.get(`${API_URL}/api/helpdesk/conversations/${convo.id}`);
    setConvo(data);
  };

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/helpdesk/conversations/${convo.id}/reply`, { content: reply });
      setReply('');
      await refresh(); onChange?.();
    } finally { setSending(false); }
  };

  const resolve = async (by = 'admin') => {
    await axios.post(`${API_URL}/api/helpdesk/conversations/${convo.id}/resolve`, { resolved_by: by });
    await refresh(); onChange?.();
  };
  const del = async () => {
    if (!window.confirm('Permanently delete this conversation?')) return;
    await axios.delete(`${API_URL}/api/helpdesk/conversations/${convo.id}`);
    setConvo(null); onChange?.();
  };
  const addTag = async () => {
    if (!tagInput.trim()) return;
    await axios.post(`${API_URL}/api/helpdesk/conversations/${convo.id}/tags`, { tag: tagInput.trim() });
    setTagInput(''); await refresh();
  };
  const rmTag = async (t) => {
    await axios.delete(`${API_URL}/api/helpdesk/conversations/${convo.id}/tags/${encodeURIComponent(t)}`);
    await refresh();
  };
  const addNote = async () => {
    if (!noteInput.trim()) return;
    await axios.post(`${API_URL}/api/helpdesk/conversations/${convo.id}/notes`, { content: noteInput });
    setNoteInput(''); await refresh();
  };

  return (
    <Card className="bg-card border-border flex flex-col" data-testid="convo-detail">
      <div className="p-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-sm text-foreground">{convo.user_name} · {convo.user_email}</div>
            <div className="text-[11px] text-muted-foreground">{convo.subject}</div>
          </div>
          <div className="flex items-center gap-1">
            <StatusPill status={convo.status} />
            {convo.status !== 'resolved' && (
              <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-400" onClick={() => resolve('admin')} data-testid="convo-resolve">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Resolve
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-400" onClick={del}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap items-center gap-1 mt-2">
          {(convo.tags || []).map(t => (
            <span key={t} className="text-[10px] bg-muted text-foreground px-1.5 py-0.5 rounded flex items-center gap-1">
              #{t} <button onClick={() => rmTag(t)}><X className="h-2.5 w-2.5" /></button>
            </span>
          ))}
          <Input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTag()} placeholder="add tag" className="h-6 w-24 text-[10px] bg-background border-border" data-testid="tag-input" />
          <button onClick={addTag} className="text-muted-foreground hover:text-amber-400"><TagIcon className="h-3 w-3" /></button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-muted/40">
        {(convo.messages || []).map(m => (
          <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-start' : 'justify-end'}`}>
            <div className={`max-w-[80%] rounded-lg px-3 py-2 text-xs whitespace-pre-wrap ${
              m.role === 'user' ? 'bg-muted text-foreground' :
              m.is_human ? 'bg-emerald-500/20 text-emerald-100 border border-emerald-500/30' :
              'bg-blue-600 text-white'}`}>
              <div className="text-[10px] opacity-70 mb-0.5">
                {m.role === 'user' ? convo.user_name : (m.is_human ? `👤 ${m.author_name || 'Admin'}` : '🤖 AI')}
                · {new Date(m.timestamp).toLocaleString()}
              </div>
              {m.content}
            </div>
          </div>
        ))}
      </div>

      {/* Internal notes */}
      {(convo.internal_notes || []).length > 0 && (
        <div className="border-t border-border bg-amber-500/5 p-2 space-y-1 max-h-32 overflow-y-auto">
          <div className="text-[10px] uppercase text-amber-400 font-semibold">Internal notes</div>
          {convo.internal_notes.map(n => (
            <div key={n.id} className="text-[11px] text-foreground">
              <span className="text-muted-foreground">{n.author_name}:</span> {n.content}
            </div>
          ))}
        </div>
      )}

      {/* Reply */}
      <div className="border-t border-border p-2 space-y-1">
        {showMacros && macros.length > 0 && (
          <div className="bg-accent rounded p-1 flex flex-wrap gap-1">
            {macros.map(m => (
              <button key={m.id} onClick={() => { setReply(prev => prev + (prev ? '\n\n' : '') + m.content); setShowMacros(false); }}
                className="text-[10px] bg-muted hover:bg-accent px-2 py-0.5 rounded text-foreground" data-testid={`macro-${m.id}`}>
                {m.title}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-end gap-1">
          <button onClick={() => setShowMacros(s => !s)} className="text-amber-400 hover:text-amber-300 p-1" title="Insert macro" data-testid="macro-toggle">
            <Zap className="h-4 w-4" />
          </button>
          <textarea value={reply} onChange={e => setReply(e.target.value)} rows={2}
            placeholder="Type your reply (takes over from AI)…"
            className="flex-1 bg-background border border-border text-foreground text-xs rounded p-2 resize-none"
            data-testid="convo-reply" />
          <Button size="sm" onClick={send} disabled={sending || !reply.trim()} className="bg-blue-600 hover:bg-blue-700 h-9" data-testid="convo-send">
            {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          </Button>
        </div>
        <div className="flex items-end gap-1">
          <StickyNote className="h-3 w-3 text-muted-foreground mt-1.5" />
          <Input value={noteInput} onChange={e => setNoteInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addNote()}
            placeholder="Internal note (not visible to customer)…"
            className="bg-background border-border h-8 text-[11px]" data-testid="note-input" />
        </div>
      </div>
    </Card>
  );
}

// ---- Tickets ---------------------------------------------------------------

function TicketsPane() {
  const [tickets, setTickets] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ subject: '', description: '', priority: 'medium' });

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/api/helpdesk/tickets`);
    // Newest-first alone puts resolved and closed tickets at the top of the
    // queue, burying the only rows an agent can act on. Order by how much
    // attention a ticket still wants, then by recency within each band.
    const RANK = { open: 0, in_progress: 1, resolved: 2, closed: 3 };
    const rank = (t) => (RANK[t.status] ?? 0);
    setTickets([...(data.tickets || [])].sort(
      (a, b) => rank(a) - rank(b) ||
                String(b.created_at || '').localeCompare(String(a.created_at || ''))));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    await axios.post(`${API_URL}/api/helpdesk/tickets`, form);
    setShowNew(false); setForm({ subject: '', description: '', priority: 'medium' });
    load();
  };
  const setStatus = async (t, status) => {
    await axios.put(`${API_URL}/api/helpdesk/tickets/${t.id}`, { status });
    load();
  };

  return (
    <Card className="bg-card border-border">
      <div className="p-3 flex items-center justify-between border-b border-border">
        <div className="text-sm font-semibold text-foreground">Tickets</div>
        <Button size="sm" onClick={() => setShowNew(true)} className="bg-blue-600 hover:bg-blue-700 text-white h-7 text-xs" data-testid="new-ticket">
          <Plus className="h-3 w-3 mr-1" /> New ticket
        </Button>
      </div>
      {showNew && (
        <div className="p-3 border-b border-border space-y-2 bg-muted/20">
          <Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="Subject" className="bg-background border-border h-8 text-xs" />
          <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} placeholder="Description" className="w-full bg-background border border-border rounded p-2 text-xs text-foreground" />
          <div className="flex items-center gap-2">
            <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} className="bg-background border border-border text-xs text-foreground rounded px-2 h-8">
              {['low', 'medium', 'high', 'urgent'].map(x => <option key={x} value={x}>{x}</option>)}
            </select>
            <Button size="sm" onClick={create} disabled={!form.subject.trim()} className="bg-blue-600 text-white h-7 text-xs">Create</Button>
            <Button size="sm" variant="ghost" onClick={() => setShowNew(false)} className="h-7 text-xs text-muted-foreground">Cancel</Button>
          </div>
        </div>
      )}
      <table className="w-full text-xs">
        <thead className="text-muted-foreground text-[10px] uppercase border-b border-border">
          <tr>
            <th className="text-left px-3 py-2">#</th>
            <th className="text-left px-3 py-2">Subject</th>
            <th className="text-left px-3 py-2">From</th>
            <th className="text-left px-3 py-2">Context</th>
            <th className="px-3 py-2">Priority</th>
            <th className="px-3 py-2">Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {tickets.map(t => (
            <tr key={t.id} className="border-b border-border/60 text-foreground">
              {/* Tickets raised before numbering existed carry no TK- number.
                  Showing the id's leading characters beats a blank cell: it is
                  still something an agent and a customer can quote. */}
              <td className="px-3 py-2 font-mono text-muted-foreground" title={t.id}>
                {t.ticket_number || (t.id ? t.id.slice(0, 8) : '—')}
              </td>
              <td className="px-3 py-2">
                <span className="relative inline-block px-1 -mx-1 rounded hover:bg-muted cursor-text" data-testid={`ticket-subject-${t.id}`}>
                  <InlineSubjectInput value={t.subject} onSave={async v => { await axios.put(`${API_URL}/api/helpdesk/tickets/${t.id}`, { subject: v }); load(); }} />
                </span>
              </td>
              {/* Who asked, and where they were standing when they asked. The
                  context block is captured by the ticket form and is often the
                  whole diagnosis -- it carries the tab, org, role and page. */}
              <td className="px-3 py-2 text-muted-foreground">
                <div className="text-foreground">{t.created_by_name || '—'}</div>
                {t.created_by_email && <div className="text-[10px]">{t.created_by_email}</div>}
              </td>
              <td className="px-3 py-2 text-[10px] text-muted-foreground max-w-[200px]">
                {t.context ? (
                  <div className="space-y-0.5">
                    {t.context.org_name && <div className="truncate">org: <span className="text-foreground">{t.context.org_name}</span></div>}
                    {t.context.user_role && <div className="truncate">role: <span className="text-foreground">{t.context.user_role}</span></div>}
                    {(t.context.active_tab || t.context.page_path) && (
                      <div className="truncate">page: <span className="text-foreground">{t.context.active_tab || t.context.page_path}</span></div>
                    )}
                  </div>
                ) : '—'}
              </td>
              <td className="px-3 py-2 text-center">
                <span className="relative inline-block px-1 -mx-1 rounded hover:bg-muted cursor-pointer" title="Click to change priority" data-testid={`ticket-priority-${t.id}`}>
                  {t.priority}
                  <select value={t.priority} onChange={async e => { await axios.put(`${API_URL}/api/helpdesk/tickets/${t.id}`, { priority: e.target.value }); load(); }}
                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full">
                    {['low', 'medium', 'high', 'urgent'].map(x => <option key={x} value={x}>{x}</option>)}
                  </select>
                </span>
              </td>
              <td className="px-3 py-2 text-center">
                <span className="relative inline-block px-1 -mx-1 rounded hover:bg-muted cursor-pointer" title="Click to change status" data-testid={`ticket-status-${t.id}`}>
                  {t.status}
                  <select value={t.status} onChange={e => setStatus(t, e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full">
                    {['open', 'in_progress', 'resolved', 'closed'].map(x => <option key={x} value={x}>{x}</option>)}
                  </select>
                </span>
              </td>
              <td className="px-3 py-2 text-right">
                {t.status === 'open' && <Button size="sm" variant="ghost" className="h-6 text-xs text-amber-400" onClick={() => setStatus(t, 'in_progress')}>Start</Button>}
                {t.status !== 'resolved' && t.status !== 'closed' && <Button size="sm" variant="ghost" className="h-6 text-xs text-emerald-400" onClick={() => setStatus(t, 'resolved')}>Resolve</Button>}
              </td>
            </tr>
          ))}
          {tickets.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No tickets.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

// ---- KB --------------------------------------------------------------------

function KBPane() {
  const [content, setContent] = useState('');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    axios.get(`${API_URL}/api/helpdesk/knowledge-base`).then(({ data }) => setContent(data.content || ''));
  }, []);
  const save = async () => {
    await axios.put(`${API_URL}/api/helpdesk/knowledge-base`, { content });
    setSaved(true); setTimeout(() => setSaved(false), 1500);
  };
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-foreground">Knowledge Base</div>
          <div className="text-[11px] text-muted-foreground">Contents are injected into the AI system prompt on every chat.</div>
        </div>
        <textarea value={content} onChange={e => setContent(e.target.value)} rows={20}
          placeholder="Paste product docs, FAQ, workflows, policies…"
          className="w-full bg-background border border-border text-foreground rounded p-3 text-xs font-mono"
          data-testid="kb-editor" />
        <div className="flex items-center gap-2">
          <Button onClick={save} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="kb-save">Save knowledge base</Button>
          {saved && <span className="text-xs text-emerald-400">Saved ✓</span>}
        </div>
      </CardContent>
    </Card>
  );
}

function LibraryPane() {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-6 text-center text-sm text-muted-foreground">
        <Library className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        Library (help documents + file attachments) — coming in v5.8.
      </CardContent>
    </Card>
  );
}

// ---- Macros ----------------------------------------------------------------

function MacrosPane({ onChange }) {
  const [macros, setMacros] = useState([]);
  const [form, setForm] = useState({ title: '', content: '', category: 'general' });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/api/helpdesk/canned-responses`);
    setMacros(data.macros || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ title: '', content: '', category: 'general' });
    setEditingId(null);
    setShowForm(true);
  };
  const edit = (m) => {
    setForm({ title: m.title, content: m.content, category: m.category || 'general' });
    setEditingId(m.id);
    setShowForm(true);
  };
  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    if (editingId) {
      await axios.put(`${API_URL}/api/helpdesk/canned-responses/${editingId}`, form);
    } else {
      await axios.post(`${API_URL}/api/helpdesk/canned-responses`, form);
    }
    setForm({ title: '', content: '', category: 'general' });
    setEditingId(null);
    setShowForm(false);
    load(); onChange?.();
  };
  const del = async (id) => {
    if (!window.confirm('Delete this macro?')) return;
    await axios.delete(`${API_URL}/api/helpdesk/canned-responses/${id}`);
    load(); onChange?.();
  };

  return (
    <Card className="bg-card border-border">
      <div className="flex items-start justify-between p-4 border-b border-border">
        <div>
          <div className="text-base font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" /> Canned Responses (Macros)
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Pre-written replies an agent can insert with one click during a live conversation.
          </div>
        </div>
        <Button size="sm" onClick={openNew} data-testid="add-macro-btn"
          className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white shrink-0">
          <Plus className="w-4 h-4 mr-1.5" /> New Macro
        </Button>
      </div>

      <CardContent className="p-4">
        {/* Create/edit sits behind the button, as BOP's dialog does, so the
            grid is not permanently sharing the page with an empty form. */}
        {showForm && (
          <div className="mb-4 p-3 rounded-xl border border-border bg-muted/30 space-y-2"
            data-testid="macro-form">
            <div className="text-sm font-semibold">{editingId ? 'Edit macro' : 'New macro'}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Input value={form.title} onChange={e => setForm(p2 => ({ ...p2, title: e.target.value }))}
                placeholder="Title (e.g. 'Changes not on the website')" data-testid="macro-title" />
              <Input value={form.category} onChange={e => setForm(p2 => ({ ...p2, category: e.target.value }))}
                placeholder="Category (e.g. website)" />
            </div>
            <textarea value={form.content} onChange={e => setForm(p2 => ({ ...p2, content: e.target.value }))}
              rows={5} placeholder="The reply an agent will insert…"
              className="w-full bg-background border border-border rounded-lg p-2 text-xs text-foreground"
              data-testid="macro-content" />
            <div className="flex gap-2">
              <Button size="sm" onClick={save} disabled={!form.title.trim() || !form.content.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40" data-testid="macro-save">
                {editingId ? 'Update macro' : 'Create macro'}
              </Button>
              <Button size="sm" variant="outline"
                onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</Button>
            </div>
          </div>
        )}

        {macros.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-4">
              <Zap className="w-7 h-7 text-amber-400" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">No macros yet</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-[320px]">
              Create canned responses for the questions that keep coming back. Agents insert them
              during a live conversation.
            </p>
            <Button size="sm" onClick={openNew} className="mt-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white">
              <Plus className="w-4 h-4 mr-1.5" /> Create your first macro
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {macros.map(m => (
              <Card key={m.id} className="border rounded-xl hover:border-indigo-200 hover:shadow-sm transition-all"
                data-testid={`macro-card-${m.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Zap className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <h4 className="text-sm font-semibold truncate">{m.title}</h4>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 rounded-md"
                        onClick={() => edit(m)} data-testid={`macro-edit-${m.id}`}>
                        <PenLine className="w-3 h-3 text-muted-foreground" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 rounded-md hover:text-red-500"
                        onClick={() => del(m.id)} data-testid={`macro-del-${m.id}`}>
                        <Trash2 className="w-3 h-3 text-muted-foreground" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2 leading-relaxed line-clamp-3">{m.content}</p>
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-[10px] border-border">{m.category || 'general'}</Badge>
                    {/* Usage is tracked by POST /canned-responses/{id}/use. Showing it is
                        the point of tracking it: it is how you find the macros nobody uses. */}
                    {m.usage_count > 0 && (
                      <span className="text-[10px] text-muted-foreground">Used {m.usage_count}x</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ConfigPane({ onChange }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/helpdesk/config`).then(({ data }) => setCfg(data));
  }, []);
  const patch = (k, v) => setCfg(p => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/helpdesk/config`, {
        ai_model: cfg.ai_model, ai_provider: cfg.ai_provider,
        system_prompt: cfg.system_prompt,
        auto_close_enabled: cfg.auto_close_enabled, auto_close_days: Number(cfg.auto_close_days),
      });
      setMsg('Saved ✓'); setTimeout(() => setMsg(''), 1500); onChange?.();
    } finally { setSaving(false); }
  };
  const runAutoClose = async () => {
    const { data } = await axios.post(`${API_URL}/api/helpdesk/admin/auto-close-inactive`);
    setMsg(`Auto-closed ${data.closed} conversations`); setTimeout(() => setMsg(''), 2500);
    onChange?.();
  };

  if (!cfg) return <div className="text-muted-foreground text-sm p-6">Loading…</div>;

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4 space-y-3">
        <div className="text-sm font-semibold text-foreground">AI model</div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Provider</label>
            <select value={cfg.ai_provider} onChange={e => patch('ai_provider', e.target.value)} className="w-full bg-background border border-border text-foreground h-9 px-2 rounded text-sm" data-testid="cfg-provider">
              <option value="anthropic">anthropic</option>
              <option value="openai">openai</option>
              <option value="gemini">gemini</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Model</label>
            <Input value={cfg.ai_model || ''} onChange={e => patch('ai_model', e.target.value)} className="bg-background border-border" data-testid="cfg-model" />
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase text-muted-foreground">System prompt (AI personality + behavior)</label>
          <textarea value={cfg.system_prompt || ''} onChange={e => patch('system_prompt', e.target.value)} rows={10} className="w-full bg-background border border-border rounded p-2 text-xs text-foreground font-mono" data-testid="cfg-prompt" />
        </div>

        <div className="text-sm font-semibold text-foreground pt-2">Auto-close</div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-foreground">
            <input type="checkbox" checked={!!cfg.auto_close_enabled} onChange={e => patch('auto_close_enabled', e.target.checked)} data-testid="cfg-autoclose" />
            Enable auto-close
          </label>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            after
            <Input type="number" min={1} max={90} value={cfg.auto_close_days} onChange={e => patch('auto_close_days', e.target.value)} className="bg-background border-border w-16 h-8" data-testid="cfg-autoclose-days" />
            days of inactivity
          </div>
          <Button size="sm" onClick={runAutoClose} variant="ghost" className="text-xs text-amber-400 h-8" data-testid="cfg-autoclose-run">
            <Clock className="h-3 w-3 mr-1" /> Run now
          </Button>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button onClick={save} disabled={saving} className="bg-blue-600 text-white" data-testid="cfg-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save config'}</Button>
          {msg && <span className="text-xs text-emerald-400">{msg}</span>}
        </div>
      </CardContent>
    </Card>
  );
}
